import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, MapPinned, X } from "lucide-react";
import { toast } from "sonner";
import { api, CITY_BY_SLUG } from "@/lib/api";

const QUESTIONS = [
  {
    q: "Your ideal Saturday morning?",
    options: [
      { label: "Wine tasting among the vines", scores: { sonoita: 2, willcox: 1 } },
      { label: "Ghost stories on historic streets", scores: { bisbee: 2, tombstone: 2 } },
      { label: "Hiking a shady canyon", scores: { hereford: 2, "sierra-vista": 1 } },
      { label: "Brunch and errands, 15 minutes from Tucson", scores: { sahuarita: 2, vail: 1 } },
    ],
  },
  {
    q: "Your housing budget and style?",
    options: [
      { label: "Land or a fixer under $200K", scores: { douglas: 2, willcox: 1, "huachuca-city": 1 } },
      { label: "A solid family home, $200–350K", scores: { "huachuca-city": 2, benson: 2 } },
      { label: "Newer suburb, $350K and up", scores: { sahuarita: 2, vail: 2 } },
      { label: "A historic charmer with character", scores: { bisbee: 2, tombstone: 1 } },
    ],
  },
  {
    q: "The commute reality?",
    options: [
      { label: "I work at or near Fort Huachuca", scores: { "sierra-vista": 2, "huachuca-city": 2, hereford: 1 } },
      { label: "My job is in Tucson", scores: { sahuarita: 2, vail: 2 } },
      { label: "I'm remote or retired", scores: { sonoita: 1, hereford: 1, bisbee: 1 } },
      { label: "I have ties across the border", scores: { douglas: 2 } },
    ],
  },
  {
    q: "Space or sidewalks?",
    options: [
      { label: "Acres, horses and a well", scores: { hereford: 2, sonoita: 1, willcox: 1 } },
      { label: "Master-planned parks and pools", scores: { sahuarita: 2, vail: 1 } },
      { label: "A walkable historic district", scores: { bisbee: 2, tombstone: 1 } },
      { label: "RV-friendly wide-open space", scores: { benson: 2, willcox: 1 } },
    ],
  },
  {
    q: "Pick a claim to fame:",
    options: [
      { label: "Hummingbird Capital of the U.S.", scores: { "sierra-vista": 2, hereford: 1 } },
      { label: "The Town Too Tough To Die", scores: { tombstone: 2 } },
      { label: "Arizona's wine country", scores: { willcox: 2, sonoita: 2 } },
      { label: "Caverns and caves", scores: { benson: 2, vail: 1 } },
    ],
  },
  {
    q: "Kids' schools are…",
    options: [
      { label: "Top priority — highest-rated district I can get", scores: { vail: 2, sahuarita: 2 } },
      { label: "Small and local suits us fine", scores: { "huachuca-city": 1, benson: 1, sonoita: 1 } },
      { label: "Not a factor for me", scores: { bisbee: 1, tombstone: 1, douglas: 1 } },
      { label: "Charter and choice options matter", scores: { "sierra-vista": 2, sahuarita: 1 } },
    ],
  },
  {
    q: "Your perfect weekend vibe?",
    options: [
      { label: "Rodeos and county fairs", scores: { sonoita: 2, douglas: 1 } },
      { label: "Art galleries and stair climbs", scores: { bisbee: 2 } },
      { label: "Gunfights and saloons", scores: { tombstone: 2 } },
      { label: "Lakes, caverns and day trips", scores: { benson: 2, "sierra-vista": 1 } },
    ],
  },
  {
    q: "Your weather wish?",
    options: [
      { label: "Cool high grasslands", scores: { sonoita: 2, bisbee: 1 } },
      { label: "Mild four gentle seasons", scores: { "sierra-vista": 2, hereford: 1 } },
      { label: "Warm desert sun, mild winters", scores: { sahuarita: 1, vail: 1, douglas: 1 } },
      { label: "Monsoon storms over the mountains", scores: { willcox: 1, "huachuca-city": 1, benson: 1 } },
    ],
  },
];

const RESULT_COPY = {
  "sierra-vista": "The Hub — Fort Huachuca, hummingbirds, and the most shopping, schools and healthcare in the region.",
  hereford: "River-valley acreage, horse properties and Ramsey Canyon birding, minutes south of Sierra Vista.",
  "huachuca-city": "The Sunset City — affordable, small-town, and minutes from the Fort Huachuca gates.",
  bisbee: "The copper city — Victorian staircases, gallery nights and ghost stories in the Mule Mountains.",
  tombstone: "The Town Too Tough To Die — the Old West, every single day.",
  benson: "The gateway — Kartchner Caverns, the Amtrak stop and easy RV-friendly living on I-10.",
  douglas: "The border city — Gadsden marble, Pancho Villa history and two-country culture.",
  willcox: "Wine country on I-10 — Rex Allen, Apple Annie's and the gateway to the Chiricahuas.",
  sahuarita: "The booming suburb — Rancho Sahuarita, top schools and 15 minutes to Tucson.",
  vail: "The school-spirit town — a top-rated district at the foot of the Rincons.",
  sonoita: "The wine-country crossroads — grasslands, rodeos, dark skies and Arizona's original AVA.",
};

function scorePicks(picks) {
  const tally = {};
  picks.forEach((opt) => {
    Object.entries(opt.scores).forEach(([k, v]) => {
      tally[k] = (tally[k] || 0) + v;
    });
  });
  return Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0] || "sierra-vista";
}

export default function QuizBand({ cityName = "" }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const done = step >= QUESTIONS.length;
  const result = useMemo(() => (done ? scorePicks(picks) : null), [done, picks]);
  const rc = result ? CITY_BY_SLUG[result] : null;

  const reset = () => {
    setStep(0);
    setPicks([]);
    setEmail("");
    setEmailed(false);
  };

  const pick = (opt) => {
    setPicks([...picks, opt]);
    setStep(step + 1);
  };

  const sendResult = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/quiz", { email, result_city: result, picks: picks.map((p) => p.label) });
      setEmailed(true);
      toast.success("Your result is on its way to your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div data-testid="town-quiz-band" className="relative overflow-hidden rounded-2xl bg-brand-navy-dark grain p-7 sm:p-9">
        <div className="absolute -left-10 -bottom-10 h-44 w-44 rounded-full bg-brand-red/20 blur-2xl" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-brand-gold font-semibold">
              <MapPinned className="h-3.5 w-3.5" /> The 60-second town quiz
            </p>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-white">Which Southeast Arizona town fits you?</h3>
            <p className="mt-2 max-w-lg text-sm sm:text-base leading-relaxed text-white/65">
              Eight quick questions about budget, commute, land and lifestyle — get matched with one of the eleven almanac towns{cityName ? ` (maybe even ${cityName})` : ""}.
            </p>
          </div>
          <button
            data-testid="quiz-open-button"
            onClick={() => { reset(); setOpen(true); }}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3.5 text-sm font-bold text-brand-navy-dark transition-opacity hover:opacity-90"
          >
            Take the quiz <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="quiz-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-navy-dark/70 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-brand-border-sand bg-brand-sand p-6 sm:p-8 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-brand-terracotta font-bold">
                  {done ? "Your result" : `Question ${step + 1} of ${QUESTIONS.length}`}
                </p>
                <button data-testid="quiz-close" onClick={() => setOpen(false)} aria-label="Close quiz"
                  className="rounded-full p-1.5 text-brand-slate/60 transition-colors hover:bg-brand-sand-dark hover:text-brand-red">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-brand-sand-dark">
                <div className="h-full rounded-full bg-gradient-to-r from-brand-gold to-brand-red transition-all duration-500"
                  style={{ width: `${(Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100}%` }} />
              </div>

              {!done ? (
                <div className="mt-6">
                  <p data-testid="quiz-question" className="font-display text-xl sm:text-2xl font-bold text-brand-navy-dark">{QUESTIONS[step].q}</p>
                  <div className="mt-5 space-y-2.5">
                    {QUESTIONS[step].options.map((opt, i) => (
                      <button
                        key={i}
                        data-testid={`quiz-option-${i}`}
                        onClick={() => pick(opt)}
                        className="w-full rounded-xl border border-brand-border-sand bg-white px-5 py-3.5 text-left text-sm font-semibold text-brand-navy-dark transition-all hover:-translate-y-0.5 hover:border-brand-navy/40 hover:shadow-md"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div data-testid="quiz-result" className="mt-6 text-center">
                  <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-terracotta font-bold">Your Southeast Arizona match</p>
                  <p data-testid="quiz-result-city" className="mt-2 font-display text-4xl font-black text-brand-navy-dark">{rc.name}</p>
                  <p className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-brand-slate/60">ZIP {rc.zips}</p>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-brand-slate">{RESULT_COPY[result]}</p>
                  <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                    <Link data-testid="quiz-explore-link" to={rc.path} onClick={() => setOpen(false)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-dark">
                      Explore the {rc.name} Almanac <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                  {emailed ? (
                    <p data-testid="quiz-email-success" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-gold-warm">
                      <CheckCircle2 className="h-4 w-4" /> Result emailed — check your inbox.
                    </p>
                  ) : (
                    <form onSubmit={sendResult} className="mt-6 flex items-center gap-2 rounded-full border border-brand-border-sand bg-white p-1.5">
                      <input
                        data-testid="quiz-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email me my result (optional)"
                        className="w-full bg-transparent px-3.5 text-sm text-brand-navy-dark placeholder:text-brand-slate/50 focus:outline-none"
                      />
                      <button data-testid="quiz-email-submit" type="submit" disabled={busy}
                        className="shrink-0 rounded-full bg-brand-red px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-brand-red-dark disabled:opacity-60">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send"}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
